import {
  configApiRef,
  createApiRef,
  discoveryApiRef,
  oauthRequestApiRef,
  type BackstageIdentityApi,
  type OAuthApi,
  type OpenIdConnectApi,
  type ProfileInfoApi,
  type SessionApi,
} from '@backstage/core-plugin-api';
import { OAuth2 } from '@backstage/core-app-api';
import { SignInPage } from '@backstage/core-components';
import {
  ApiBlueprint,
  createFrontendModule,
} from '@backstage/frontend-plugin-api';
import { SignInPageBlueprint } from '@backstage/plugin-app-react';

// The generic `oidcAuthApiRef` was removed upstream ("too generic"), so apps
// must define their own. This is the Keycloak (governance realm) OIDC client.
export const oidcAuthApiRef = createApiRef<
  OAuthApi &
    OpenIdConnectApi &
    ProfileInfoApi &
    BackstageIdentityApi &
    SessionApi
>({
  id: 'auth.oidc',
});

// Register the OIDC auth Utility API in the new frontend system.
const oidcAuthApi = ApiBlueprint.make({
  name: 'oidc-auth',
  params: defineParams =>
    defineParams({
      api: oidcAuthApiRef,
      deps: {
        discoveryApi: discoveryApiRef,
        oauthRequestApi: oauthRequestApiRef,
        configApi: configApiRef,
      },
      factory: ({ discoveryApi, oauthRequestApi, configApi }) =>
        OAuth2.create({
          discoveryApi,
          oauthRequestApi,
          provider: {
            id: 'oidc',
            // The PROVIDER's name, not the sentence: core-components renders
            // this bare in the OAuth popup list and inside "The {title}
            // provider is not configured...". The whole-sentence
            // gov_brand_sso_label belongs on the SignInPage card below.
            title: 'ASG Corporate',
            icon: () => null,
          },
          environment: configApi.getOptionalString('auth.environment'),
        }),
    }),
});

// Replace the default sign-in page with one that authenticates via Keycloak.
const signInPage = SignInPageBlueprint.make({
  params: {
    loader: async () => props =>
      (
        <SignInPage
          {...props}
          provider={{
            id: 'oidc',
            // gov_brand_sso_label (globals/branding.yml). Backstage renders
            // this string verbatim as the provider card title, so it carries
            // the whole sentence - unlike the Keycloak realm displayName,
            // whose template prepends "Sign in to" itself.
            title: 'Sign in to ASG Corporate',
            message: 'Use your ASG Corporate account (Keycloak SSO)',
            apiRef: oidcAuthApiRef,
          }}
        />
      ),
  },
});

export const authModule = createFrontendModule({
  pluginId: 'app',
  extensions: [oidcAuthApi, signInPage],
});
